/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import personFields from "../modules/personFields";
import * as stagesCase from "../../../Stages/StagesCase";
import { dateFieldDefault } from "../../../validationRules";
import { stageFormDefinition as contractStageFormDefinition } from "../contract/stageFormDefinition";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { BackofficeUser } from "../../../../types/BackofficeUser";
import _ from "lodash";
import { objectivesLawFirm } from "../modules/objectiveLawfirmValues";
import { arrayToSelectOptions } from "../../../formServiceFunctions";
import { abstractLitigationStageFormDefinition } from "../abstractLitigationCase/stageFormDefinition";
import { RentalContract } from "../../../../types/Products/RentalContract";

export const stageFormDefinition = (
  product: RentalContract,
  currentUser?: BackofficeUser,
): ReturnType<FormDefinition<RentalContract>> => {
  const personalTextMessageField: FormElementPropsWithoutProduct<RentalContract> = {
    type: "ExpandableTextField",
    path: `${product.productClassName}.personalTextMessage`,
    isDisabled: () => true,
    additionalProps: {
      multiline: true,
    },
  };
  const createdFields: FormElementPropsWithoutProduct<RentalContract>[] = [
    ...personFields({ path: `${product.productClassName}.client` }),
    personalTextMessageField,
  ];

  const dataCompletedFields: FormElementPropsWithoutProduct<RentalContract>[] = [
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.housingDeficienciesDescriptionClient`,
      isDisabled: () => true,
      isHidden: ({ product, values }) =>
        product.advisoryHandling ||
        product.isLawsuitHandling ||
        !(values.rentalContract.reasonForDispute === "housingDeficiencies"),
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.rentalContractStartDate`,
      validators: dateFieldDefault,
      isDisabled: ({ product }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      isHidden: ({ values }) =>
        values.rentalContract.reasonForDispute !== "utilitiesStatement" &&
        values.rentalContract.reasonForDispute !== "deposit" &&
        values.rentalContract.reasonForDispute !== "landlordHasCancelled",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.rentalContractEndDate`,
      validators: dateFieldDefault,
      isDisabled: ({ product }) => (product.stage || 0) >= stagesCase.STAGE_CHECKED,
      isHidden: ({ values }) =>
        values.rentalContract.reasonForDispute !== "utilitiesStatement" &&
        values.rentalContract.reasonForDispute !== "deposit" &&
        values.rentalContract.reasonForDispute !== "landlordHasCancelled",
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.housingDeficienciesDescriptionLawFirm`,
      isHidden: ({ product, values }) =>
        product.advisoryHandling ||
        product.caseCategory !== "enforceClaim" ||
        product.isLawsuitHandling ||
        !(values.rentalContract.reasonForDispute === "housingDeficiencies"),
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategory`,
      isDisabled: ({ product }) => product.caseCategory === "advisory",
      options: ({ product }) => {
        return product.caseCategory === "advisory"
          ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
          : [{ value: "enforceClaim" }, { value: "defendClaim" }];
      },

      alert: {
        onChange:
          "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
          "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
          "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
          "der Mandantschaft im Verfahren tatsächlich ändern?",
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.defenseProcessStatus`,
      isDisabled: ({ product }) => product.stage! >= stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
      options: () => [
        { value: "reminder" },
        { value: "debtCollection" },
        { value: "paymentOrder" },
        { value: "enforcementOrder" },
        { value: "lawsuit" },
        { value: "none" },
      ],
    },
    personalTextMessageField,
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.paymentOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.enforcementOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_DATA_COMPLETED],
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectiveLawFirm`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" || product.isLawsuitHandling,
      options: ({ product }) => {
        if (_.isEmpty(product.possibleObjectives)) {
          return objectivesLawFirm;
        } else {
          return arrayToSelectOptions(product.possibleObjectives);
        }
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectiveLawFirmSecondary`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" || product.isLawsuitHandling,
      options: ({ product }) => {
        if (_.isEmpty(product.possibleObjectives)) {
          return objectivesLawFirm;
        } else {
          return arrayToSelectOptions(product.possibleObjectives);
        }
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectiveLawFirmTertiary`,
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategoryAssessment === "reject" || product.isLawsuitHandling,
      options: ({ product }) => {
        if (_.isEmpty(product.possibleObjectives)) {
          return objectivesLawFirm;
        } else {
          return arrayToSelectOptions(product.possibleObjectives);
        }
      },
    },
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.claimedAmountLawFirm`,
      isDisabled: ({ product }) => product.stage! >= stagesCase.STAGE_CHECKED,
      isHidden: ({ product }) => product.advisoryHandling || product.isLawsuitHandling,
      fieldType: "money",
      numberType: "float",
    },
  ];

  const dataLawFirmCompletedFields: FormElementPropsWithoutProduct<RentalContract>[] = [
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.housingDeficienciesDescriptionLawFirm`,
      isHidden: ({ product, values }) =>
        product.advisoryHandling ||
        product.caseCategory !== "enforceClaim" ||
        product.isLawsuitHandling ||
        !(values.rentalContract.reasonForDispute === "housingDeficiencies"),
      additionalProps: {
        multiline: true,
      },
    },
    ...contractStageFormDefinition(product)[stagesCase.STAGE_DATA_COMPLETED_LAWFIRM],
    personalTextMessageField,
  ];

  const checkedFields: FormElementPropsWithoutProduct<RentalContract>[] = [
    ...contractStageFormDefinition(product, currentUser)[stagesCase.STAGE_CHECKED],
    personalTextMessageField,
  ];

  return {
    [stagesCase.STAGE_CREATED]: createdFields,
    [stagesCase.STAGE_DATA_COMPLETED]: dataCompletedFields,
    [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: dataCompletedFields,
    [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: dataLawFirmCompletedFields,
    [stagesCase.STAGE_CHECKED]: checkedFields,
    [stagesCase.STAGE_IN_PROGRESS]: contractStageFormDefinition(product, currentUser)[stagesCase.STAGE_IN_PROGRESS],
    [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]:
      contractStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT],
    [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]:
      contractStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED],
    [stagesCase.STAGE_ACTION_REQUIRED]: contractStageFormDefinition(product)[stagesCase.STAGE_ACTION_REQUIRED],
    [stagesCase.STAGE_PREPARE_LAWSUIT]: contractStageFormDefinition(product, currentUser)[
      stagesCase.STAGE_PREPARE_LAWSUIT
    ],
    [stagesCase.STAGE_LAWSUIT]: contractStageFormDefinition(product, currentUser)[stagesCase.STAGE_LAWSUIT],
    [stagesCase.STAGE_TRIAL]: contractStageFormDefinition(product, currentUser)[stagesCase.STAGE_LAWSUIT],
    [stagesCase.STAGE_EVALUATE_JUDGEMENT]: contractStageFormDefinition(product, currentUser)[
      stagesCase.STAGE_EVALUATE_JUDGEMENT
    ],
    [stagesCase.STAGE_LAWSUIT_COMPLETED]: contractStageFormDefinition(product, currentUser)[
      stagesCase.STAGE_LAWSUIT_COMPLETED
    ],
    [stagesCase.STAGE_COMPLETED]: contractStageFormDefinition(product, currentUser)[stagesCase.STAGE_COMPLETED],
  };
};
